import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/v2/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('hb9g_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.includes('/auth')) {
      localStorage.removeItem('hb9g_token');
      localStorage.removeItem('hb9g_user');
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data)
};

export const qsoAPI = {
  getAll: () => api.get('/qso'),
  myProposals: () => api.get('/qso/my-proposals'),
  leaderboard: () => api.get('/qso/leaderboard'),
  propose: (qsoId, nomLibre, indicatifLibre) => api.post(`/qso/${qsoId}/propose`, nomLibre ? { nom_libre: nomLibre, indicatif_libre: indicatifLibre } : {}),
  cancel: (qsoId) => api.delete(`/qso/${qsoId}/cancel`),
  validate: (qsoId, approved, reason) => api.post(`/qso/${qsoId}/validate`, { approved, reason })
};

export const annonceAPI = {
  getAll: (params) => api.get('/annonces', { params }),
  getRefs: () => api.get('/annonces/refs'),
  getOne: (annonceId) => api.get(`/annonces/${annonceId}`),
  getMy: () => api.get('/annonces/my'),
  getPending: () => api.get('/annonces/pending'),
  create: (formData) => api.post('/annonces', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (annonceId, formData) => api.put(`/annonces/${annonceId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  renew: (annonceId, duree_mois) => api.post(`/annonces/${annonceId}/renew`, { duree_mois }),
  withdraw: (annonceId) => api.post(`/annonces/${annonceId}/withdraw`),
  delete: (annonceId) => api.delete(`/annonces/${annonceId}`),
  validate: (annonceId, approved, reason) => api.post(`/annonces/${annonceId}/validate`, { approved, reason })
};

export const evenementAPI = {
  getAll: () => api.get('/evenements'),
  getMy: () => api.get('/evenements/my'),
  getPending: () => api.get('/evenements/pending'),
  create: (data) => api.post('/evenements', data),
  update: (evenementId, data) => api.put(`/evenements/${evenementId}`, data),
  delete: (evenementId) => api.delete(`/evenements/${evenementId}`),
  validate: (evenementId, approved, reason) => api.post(`/evenements/${evenementId}/validate`, { approved, reason })
};

export const stammAPI = {
  getAll: () => api.get('/stamm'),
  getMy: () => api.get('/stamm/my'),
  proposer: (sessionId, data) => api.post(`/stamm/${sessionId}/proposer`, data || {}),
  retirer: (sessionId) => api.delete(`/stamm/${sessionId}/proposer`),
  // admin
  getAllAdmin: () => api.get('/stamm/admin'),
  valider: (sessionId, approved, reason) => api.post(`/stamm/${sessionId}/valider`, { approved, reason }),
  annulerSession: (sessionId, raison) => api.post(`/stamm/${sessionId}/annuler`, { raison }),
  rouvrirSession: (sessionId) => api.post(`/stamm/${sessionId}/rouvrir`),
  createSession: (data) => api.post('/stamm', data),
  deleteSession: (sessionId) => api.delete(`/stamm/${sessionId}`),
};

export const publicAPI = {
  getModules: () => api.get('/admin/settings/modules'),
};

export const settingsAPI = {
  get: () => api.get('/admin/settings'),
  update: (data) => api.put('/admin/settings', data),
  testEmail: (to) => api.post('/admin/settings/test-email', { to }),
  testSignal: () => api.post('/admin/settings/test-signal'),
  signalRegister: (number, captcha) => api.post('/admin/settings/signal/register', captcha ? { number, captcha } : { number }),
  signalVerify: (number, code) => api.post('/admin/settings/signal/verify', { number, code }),
  signalListGroups: (number) => api.get('/admin/settings/signal/groups', { params: { number } }),
  signalCreateGroup: (number, name) => api.post('/admin/settings/signal/create-group', { number, name }),
  signalJoinGroup: (number, inviteLink) => api.post('/admin/settings/signal/join-group', { number, inviteLink }),
  signalUnregister: (number) => api.delete('/admin/settings/signal/unregister', { data: { number } }),
  signalResetGroupLink: (number, groupId) => api.post('/admin/settings/signal/reset-link', { number, groupId }),
};

export const adminAPI = {
  getUsers: () => api.get('/auth/admin/users'),
  getPendingUsers: () => api.get('/auth/admin/pending-users'),
  updateUserRole: (userId, role) => api.put(`/auth/admin/users/${userId}/role`, { role }),
  approveUser: (userId) => api.post(`/auth/admin/users/${userId}/approve`),
  disableUser: (userId, disabled) => api.patch(`/auth/admin/users/${userId}/disable`, { disabled }),
  deleteUser: (userId) => api.delete(`/auth/admin/users/${userId}`),
  getQSOAll: () => api.get('/qso/admin'),
  createQSOSession: (data) => api.post('/qso/admin', data),
  deleteQSOSession: (qsoId) => api.delete(`/qso/admin/${qsoId}`),
  releaseQSOSession: (qsoId) => api.post(`/qso/admin/${qsoId}/release`),
  generateQSOSessions: () => api.post('/qso/admin/generate'),
};

export default api;
